import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useMantineColorScheme } from '@mantine/core';

/**
 * ColorSchemeManager - Manages color scheme based on current route
 * 
 * This component ensures that:
 * 1. Admin routes always use light mode
 * 2. Menu routes use the restaurant's color scheme (set by Menu/MenuChat components)
 * 3. All other routes default to light mode
 * 
 * Uses useLayoutEffect to set color scheme synchronously before browser paint,
 * preventing flash of inaccurate color scheme (FART).
 * 
 * This is the SINGLE SOURCE OF TRUTH for color scheme management (except menu routes).
 * Other components should NOT set color scheme to avoid conflicts.
 */
export function ColorSchemeManager() {
  const location = useLocation();
  const { setColorScheme } = useMantineColorScheme();

  useLayoutEffect(() => {
    const currentPath = location.pathname;
    
    // Skip menu routes entirely - Menu/MenuChat components handle their own color schemes
    if (currentPath.startsWith('/menus/')) {
      return; // Don't interfere with menu routes
    }
    
    // For non-menu routes, always use light mode
    // Set both via Mantine's API and directly on document element
    // This ensures consistency and prevents flash
    // setColorScheme('light') persists to localStorage via the color scheme manager
    setColorScheme('light');
    document.documentElement.setAttribute('data-mantine-color-scheme', 'light');
  }, [location.pathname, setColorScheme]);

  return null; // This component doesn't render anything
}
